import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const ORANGE = theme.orange;
const BLACK  = theme.black;

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

const ROLES = ['manager','souscripteur','accounts','it','courtier','client','voyageur'];

const lbl = { fontSize:12, color:'#64748B', display:'block', marginBottom:6, fontWeight:500 };
const inp = { border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', width:'100%', fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none' };

function Field({ label, value, onChange, type='text', placeholder, disabled }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={lbl}>{label}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
        style={{ ...inp,
          borderColor: focus ? ORANGE : '#D9D9D5',
          boxShadow: focus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none',
          background: disabled ? '#F7F7F5' : '#fff',
          color: disabled ? '#94A3B8' : '#1A1A1A',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'all .12s' }}/>
    </div>
  );
}

export default function FicheUtilisateur() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isEdit = Boolean(id);

  const [nom,    setNom]    = useState('');
  const [email,  setEmail]  = useState('');
  const [role,   setRole]   = useState('souscripteur');
  const [statut, setStatut] = useState('actif');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [intermediaireId, setIntermediaireId] = useState('');
  const [intermediaires, setIntermediaires] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [resetInfo, setResetInfo] = useState(null);
  // Mode de création : 'link' (lien d'activation) ou 'password' (mot de passe défini par l'IT)
  const [createMode, setCreateMode] = useState('link');
  const [password, setPassword]     = useState('');

  // Charger la liste des clients (pour rattachement si rôle = client)
  useEffect(() => {
    authFetch(`${BASE}/clients`).then(r => r.json()).then(setClients).catch(()=>{});
  }, []);

  // Charger la liste des courtiers/intermédiaires (pour rattachement si rôle = courtier)
  useEffect(() => {
    authFetch(`${BASE}/intermediaires`).then(r => r.json()).then(d => setIntermediaires(Array.isArray(d) ? d : (d.items || []))).catch(()=>{});
  }, []);

  // Charger l'utilisateur en édition
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    authFetch(`${BASE}/utilisateurs/${id}`).then(r => r.json()).then(d => {
      if (d.error) { setError(d.error); return; }
      setNom(d.nom || '');
      setEmail(d.email || '');
      setRole(d.role || 'souscripteur');
      setStatut(d.statut || 'actif');
      setClientId(d.client_id || '');
      setIntermediaireId(d.intermediaire_id || '');
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  async function save() {
    setSaving(true); setError(''); setSuccess('');
    try {
      const body = { nom, email, role, statut };
      if (role === 'client') body.client_id = clientId || null;
      if (role === 'courtier') body.intermediaire_id = intermediaireId || null;
      // Mode mot de passe : l'IT définit directement le mot de passe (pas de lien d'activation)
      if (!isEdit && createMode === 'password') {
        if (!password || password.length < 8) {
          setError(t('utilisateurs.pwd_too_short'));
          setSaving(false);
          return;
        }
        body.password = password;
        body.send_activation_email = false;
      }
      const url = isEdit ? `${BASE}/utilisateurs/${id}` : `${BASE}/utilisateurs`;
      const method = isEdit ? 'PUT' : 'POST';
      const r = await authFetch(url, {
        method, headers:{'Content-Type':'application/json'},
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); return; }
      setSuccess(isEdit ? t('utilisateurs.saved') : t('utilisateurs.created'));
      if (!isEdit) {
        setTimeout(() => navigate(`/utilisateurs/${data.id}`), 800);
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function sendReset() {
    try {
      const r = await authFetch(`${BASE}/utilisateurs/${id}/reset-password`, { method:'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      setResetInfo({ simulated: data.email_simulated });
      setTimeout(() => setResetInfo(null), 5000);
    } catch (e) { setError(e.message); }
  }

  async function handleDelete() {
    if (!window.confirm(t('utilisateurs.delete_confirm') + ' ' + nom + ' ?')) return;
    try {
      const r = await authFetch(`${BASE}/utilisateurs/${id}`, { method:'DELETE' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erreur');
      navigate('/utilisateurs');
    } catch (e) { setError(e.message); }
  }

  if (loading) return <div style={{ padding:24, color:'#64748B' }}>{t('common.loading')}</div>;

  return (
    <div style={{ maxWidth:720, margin:'0 auto' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
        {/* Header clair (style "Ligne") */}
        <div style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:13,
          borderBottom:`2px solid ${theme.borderDark}` }}>
          <button onClick={() => navigate('/utilisateurs')}
            style={{ background:theme.surfaceAlt, border:`0.5px solid ${theme.border}`, width:34, height:34, borderRadius:8, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <SvgIcon size={18} color={theme.textSecondary}><path d="M15 6l-6 6l6 6"/></SvgIcon>
          </button>
          <div style={{ width:42, height:42, borderRadius:'50%', background:theme.sidebarBg,
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:ORANGE, fontSize:15, fontWeight:500 }}>
            {((isEdit?nom:'')||'?').trim().charAt(0).toUpperCase() || '+'}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:theme.textTertiary, textTransform:'uppercase', letterSpacing:'.04em' }}>{t('utilisateurs.breadcrumb')}</div>
            <div style={{ fontSize:22, fontWeight:800, color:theme.text }}>
              {isEdit ? (nom || t('utilisateurs.edit_title')) : t('utilisateurs.new_title')}
            </div>
          </div>
          {isEdit && (
            <span style={{ fontSize:12, padding:'5px 13px', borderRadius:99, fontWeight:500,
              display:'inline-flex', alignItems:'center', gap:6,
              background: statut==='actif'?'#E1F5EE':'#FCEBEB', color: statut==='actif'?'#0F6E56':'#A32D2D' }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background: statut==='actif'?'#1D9E75':'#E24B4A' }}/>
              {statut==='actif' ? t('utilisateurs.statut_actif') : t('utilisateurs.statut_inactif')}
            </span>
          )}
        </div>

        {/* Formulaire */}
        <div style={{ padding:20 }}>
          {error && <div style={{ background:'#FCEBEB', border:'1px solid #F09595', borderRadius:8, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#A32D2D' }}>⚠ {error}</div>}
          {success && <div style={{ background:'#E1F5EE', border:'1px solid #B6E5D2', borderRadius:8, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#0F6E56' }}>✓ {success}</div>}
          {resetInfo && (
            <div style={{ background:'#E1F5EE', border:'1px solid #B6E5D2', borderRadius:8, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#0F6E56' }}>
              ✓ {t('utilisateurs.reset_sent_simple') || 'Lien envoyé'}
              {resetInfo.simulated && <span style={{ marginLeft:8, fontStyle:'italic', color:'#5F5E5A' }}>(mode simulé — vérifier la console)</span>}
            </div>
          )}

          <Field label={t('utilisateurs.field_nom')} value={nom} onChange={setNom} placeholder="Ex. Patrick Martin"/>
          <Field label={t('utilisateurs.field_email')} value={email} onChange={setEmail} type="email" placeholder="prenom.nom@arcus-solutions.fr"/>

          <div style={{ marginBottom:14 }}>
            <label style={lbl}>{t('utilisateurs.field_role')}</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              style={{ ...inp, cursor:'pointer' }}>
              {ROLES.map(r => <option key={r} value={r}>{t('nav.role_'+r)}</option>)}
            </select>
          </div>

          {role === 'client' && (
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>{t('utilisateurs.field_client')}</label>
              <select value={clientId || ''} onChange={e => setClientId(e.target.value)}
                style={{ ...inp, cursor:'pointer' }}>
                <option value="">— {t('common.select')} —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          )}

          {role === 'courtier' && (
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>{t('utilisateurs.field_courtier')}</label>
              <select value={intermediaireId || ''} onChange={e => setIntermediaireId(e.target.value)}
                style={{ ...inp, cursor:'pointer' }}>
                <option value="">— {t('common.select')} —</option>
                {intermediaires.map(i => <option key={i.id} value={i.id}>{i.nom}</option>)}
              </select>
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <label style={lbl}>{t('utilisateurs.field_statut')}</label>
            <div style={{ display:'flex', gap:6 }}>
              {['actif','inactif'].map(s => (
                <button key={s} onClick={() => setStatut(s)}
                  style={{ flex:1, padding:'10px', borderRadius:8, fontSize:14, cursor:'pointer',
                    fontWeight: statut===s ? 500 : 400,
                    background: statut===s ? BLACK : '#fff',
                    color: statut===s ? '#fff' : '#64748B',
                    border: statut===s ? 'none' : '1px solid #D9D9D5' }}>
                  {s === 'actif' ? t('utilisateurs.statut_actif') : t('utilisateurs.statut_inactif')}
                </button>
              ))}
            </div>
          </div>

          {!isEdit && (
            <div style={{ marginTop:16 }}>
              <label style={lbl}>{t('utilisateurs.create_mode_label')}</label>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                {[['link', t('utilisateurs.mode_link')], ['password', t('utilisateurs.mode_password')]].map(([val, lbl2]) => (
                  <button key={val} type="button" onClick={() => setCreateMode(val)}
                    style={{ flex:1, padding:'9px 8px', borderRadius:8, cursor:'pointer', fontSize:13,
                      fontWeight: createMode===val ? 600 : 400,
                      background: createMode===val ? BLACK : '#fff',
                      color: createMode===val ? '#fff' : '#64748B',
                      border: createMode===val ? 'none' : '1px solid #D9D9D5' }}>
                    {lbl2}
                  </button>
                ))}
              </div>

              {createMode === 'link' ? (
                <div style={{ background:'#FFF7F2', border:'1px solid #FED7AA', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#9A5A00', display:'flex', alignItems:'center', gap:8 }}>
                  <SvgIcon size={16} color="#9A5A00"><path d="M12 8h.01M11 12h1v4h1M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18"/></SvgIcon>
                  {t('utilisateurs.create_info')}
                </div>
              ) : (
                <div>
                  <Field label={t('utilisateurs.password_label')} value={password} onChange={setPassword}
                    type="text" placeholder={t('utilisateurs.password_ph')} />
                  <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#1E40AF', display:'flex', alignItems:'center', gap:8 }}>
                    <SvgIcon size={16} color="#1E40AF"><path d="M12 8h.01M11 12h1v4h1M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0 -18"/></SvgIcon>
                    {t('utilisateurs.password_info')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding:'14px 20px', borderTop:'0.5px solid #F1F1EE', background:'#FAFAF8',
          display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:8 }}>
            {isEdit && (
              <>
                <button onClick={sendReset}
                  style={{ background:'#fff', color:'#9A5A00', border:'1px solid #FED7AA', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:7 }}>
                  <SvgIcon size={15} color="#9A5A00"><path d="M15 8h.01M6 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0M9 13l-6 6l3 0v3l3 0l3 -3l-3 -3M21 7a4 4 0 0 0 -8 0a4 4 0 0 0 8 0"/></SvgIcon>
                  {t('utilisateurs.send_reset')}
                </button>
                <button onClick={handleDelete}
                  style={{ background:'#fff', color:'#A32D2D', border:'1px solid #F09595', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:500, cursor:'pointer' }}>
                  {t('common.delete')}
                </button>
              </>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => navigate('/utilisateurs')}
              style={{ background:'#fff', color:'#64748B', border:'1px solid #D9D9D5', borderRadius:8, padding:'9px 16px', fontSize:14, cursor:'pointer' }}>
              {t('common.cancel')}
            </button>
            <button onClick={save} disabled={saving || !nom || !email || !role}
              style={{ background:ORANGE, color:BLACK, border:'none', borderRadius:8, padding:'9px 18px', fontSize:14, fontWeight:500,
                cursor:(saving||!nom||!email)?'not-allowed':'pointer', opacity:(saving||!nom||!email)?.5:1 }}>
              {saving ? '…' : (isEdit ? t('common.save') : (createMode === 'password' ? t('utilisateurs.create_btn') : t('utilisateurs.create_and_send')))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
